import { useEffect, useReducer, Fragment } from 'react'
import {
    Autocomplete,
    TextField,
    Checkbox,
    FormControlLabel,
    Chip,
    Switch,
    MenuItem,
} from '@mui/material'
import {
    Edit as EditIcon,
    StickyNote2 as StickyNote2Icon,
    DoneAll as DoneAllIcon,
} from '@mui/icons-material'
import { SaveButton, Grid, FormSection } from '../../../UniversalComponents'
import { useToastMessage } from '../../../../context/ToastPopUpContext'

import {
    editUserProfile,
    getDealers,
    getManufacturers,
    getBoats,
} from '../../../../apiRoutes'
import responseError from '../../../../context/responseError/responseError'
import { useNavMenu } from '../../../../context/NavMenuContext'
import { FEATURE_PERMISSIONS } from '../../../../utilities/featurePermissions'
import { useAuth } from '../../../../context/AuthenticationContext'

interface UserProfileFormData {
    _id: string
    id: string
    userProfileName: string
    manufacturer: object
    userProfileAssignedManufacturers: object[]
    userProfilePermissions: object
    dealerProfile: boolean
    mobileCustomerProfile: boolean
    disableDeactivation: boolean
}
interface UserProfileFormTypes {
    row: UserProfileFormData
    formDescription?: string
    userFormPermissionCheck?: boolean
    userFormPermissionBlockCheck?: boolean
    onClose?: any
    saveCache?: any
    minimizeModal?: () => void
    readOnly?: boolean
    hideDisplay?: boolean
    disableDeactivation?: boolean
}

const UserProfileForm = ({
    row,
    formDescription,
    onClose = () => {},
    saveCache = () => {},
    minimizeModal,
    userFormPermissionBlockCheck,
    userFormPermissionCheck,
    hideDisplay,
    readOnly,
}: UserProfileFormTypes) => {
    const currentUser = useAuth()
    const auth = currentUser.auth
    const setToastMessage = useToastMessage()
    const { navMenuLinks } = useNavMenu()
    // Permission rows: every nav page, then the feature permissions that
    // aren't pages (the opt-in device tabs).
    const permissionNames: string[] = navMenuLinks
        .reduce(
            (names: string[], { children }) =>
                names.concat((children || []).map(({ name }) => name)),
            []
        )
        .concat(FEATURE_PERMISSIONS)
    const defaultState = {
        formData: {
            id: row._id || '',
            _id: row._id || '',
            userProfileName: row?.userProfileName || '',
            role: (row as any)?.role || '',
            manufacturer: row?.manufacturer || {},
            userProfileAssignedManufacturers:
                row?.userProfileAssignedManufacturers || [],
            userProfilePermissions: row?.userProfilePermissions || {},
            dealerProfile: row?.dealerProfile || false,
            mobileCustomerProfile: row?.mobileCustomerProfile || false,
            disableDeactivation: row?.disableDeactivation || false,
        },
        formValidation: {
            isFormDirty: false,
            isFormValid: false,
            userProfileNameValidation: false,
            userProfileAssignedManufacturersValidation: false,
        },
        manufacturerOptions: [],
        manufacturers: [],
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const { formData, formValidation, manufacturers } = state

    const {
        id,
        _id,
        userProfileName,
        role,
        manufacturer,
        userProfilePermissions,
        userProfileAssignedManufacturers,
        dealerProfile,
        mobileCustomerProfile,
        disableDeactivation,
    } = formData

    const {
        isFormDirty,
        isFormValid,
        userProfileNameValidation,
        userProfileAssignedManufacturersValidation,
    } = formValidation

    const handleSubmit = async () => {
        try {
            // Normalize the manufacturer scope by tier: SUPER_ADMIN, USER and
            // DEALER are global (none); only MANUFACTURER is constrained to one.
            // A dealer's boat scope comes from the user's assignedDealerProfile
            // (its dealers[]), not the profile's manufacturer — so the shared
            // Dealer profile is intentionally manufacturer-agnostic. Keep the
            // single `manufacturer` field in sync with the array.
            const global =
                role === 'SUPER_ADMIN' || role === 'USER' || role === 'DEALER'
            const assigned = global
                ? []
                : userProfileAssignedManufacturers || []
            let parsedFormData = {
                ...formData,
                userProfileAssignedManufacturers: assigned,
                manufacturer: assigned[0] || null,
                // Keep the legacy tier flags in sync with the role until the
                // remaining flag-readers are migrated to `role`.
                superAdmin: role === 'SUPER_ADMIN',
                dealerProfile: role === 'DEALER',
                mobileCustomerProfile: role === 'USER',
            }
            let res: any

            res = await editUserProfile(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `User Profile: ${data.userProfileName} was ${
                            !id ? 'created' : 'edited'
                        } successfully`,
                        'success'
                    )

                    onClose(row.userProfileName, data)
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            setToastMessage(err.message, 'error')
        }
    }

    const handleReset = () =>
        setState({
            formData: defaultState.formData,

            formValidation: defaultState.formValidation,
        })

    const handleChange = (e: any) => {
        const { name, value } = e.target

        setState({
            formData: { ...formData, [name]: value },
            formValidation: { ...formValidation, isFormDirty: true },
        })
    }

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }

    const getData = async () => {
        let manufacturerOptions: any = []
        let manufacturers: any = []

        try {
            let data = await getManufacturers(true)
            let fleetData = await getBoats(true)
            const getDealerData = await getDealers(true)
            if (data && data.length !== 0) {
                manufacturerOptions = data.map(
                    ({ manufacturerName }) => manufacturerName
                )
                manufacturers = data.map(({ _id, manufacturerName }) => ({
                    _id,
                    manufacturerName,
                }))
            }

            setState({
                manufacturerOptions,
                manufacturers,
            })
        } catch (err: any) {
            setToastMessage(err, 'error')
        }
    }

    const handleUserPermissionsChange = (bool, name, type) => {
        setState({
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
            formData: {
                ...formData,
                userProfilePermissions: {
                    ...userProfilePermissions,
                    [name]: {
                        ...userProfilePermissions[name],
                        [type]: bool,
                        read: bool ? true : false,
                    },
                },
            },
        })
    }

    useEffect(() => {
        if (!readOnly) {
            getData()
        }
    }, [])

    useEffect(() => {
        if (readOnly) {
            setState({
                formData: {
                    id: row._id || '',
                    userProfileName: row?.userProfileName || '',
                    role: (row as any)?.role || '',
                    manufacturer: row?.manufacturer || {},
                    userProfileAssignedManufacturers:
                        row?.userProfileAssignedManufacturers || [],
                    userProfilePermissions: row?.userProfilePermissions || {},
                    dealerProfile: row?.dealerProfile || false,
                    mobileCustomerProfile: row?.mobileCustomerProfile || false,
                },
                formValidation: {
                    isFormDirty: false,
                    isFormValid: false,
                },
                manufacturerOptions: [],
                manufacturers: [],
            })
        }
    }, [row])

    // useEffect(() => {
    //     if (isFormDirty) {
    //         setState({
    //             formValidation: {
    //                 ...formValidation,
    //                 isFormValid: true,
    //             },
    //         })
    //     } else {
    //         setState({
    //             formValidation: {
    //                 ...formValidation,
    //                 isFormValid: false,
    //             },
    //         })
    //     }
    // }, [isFormDirty])

    useEffect(() => {
        const assignedValidation = () => {
            // Global tiers (super admin / customer / dealer) need no
            // manufacturer; only a manufacturer profile requires exactly one.
            if (
                role === 'SUPER_ADMIN' ||
                role === 'USER' ||
                role === 'DEALER' ||
                !role
            ) {
                return true
            }
            return (
                !userProfileAssignedManufacturersValidation &&
                userProfileAssignedManufacturers.length > 0
            )
        }

        if (
            isFormDirty &&
            !userProfileNameValidation &&
            userProfileName &&
            assignedValidation()
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        userProfileNameValidation,
        userProfileAssignedManufacturersValidation,
        userProfileName,
        userProfileAssignedManufacturers,
        dealerProfile,
        mobileCustomerProfile,
        userProfilePermissions,
    ])

    return (
        <Grid container spacing={2} sx={{ marginTop: 8 }}>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        md={!readOnly ? 6 : 12}
                        style={{
                            display: hideDisplay === true ? 'none' : 'flex',
                        }}
                    >
                        <FormSection
                            title="User Profile Info"
                            titleAlignment="center"
                        >
                            <Grid item xs={12}>
                                {!readOnly && (
                                    <Grid container spacing={2}>
                                        <Grid
                                            item
                                            xs={
                                                role === 'MANUFACTURER' ? 5 : 12
                                            }
                                        >
                                            <TextField
                                                required={true}
                                                label="User Profile Name"
                                                name="userProfileName"
                                                fullWidth
                                                onChange={handleChange}
                                                value={userProfileName}
                                                onBlur={validateOnBlur}
                                                disabled={readOnly}
                                                helperText={
                                                    userProfileNameValidation &&
                                                    !userProfileName
                                                        ? 'Please Enter a profile name'
                                                        : ''
                                                }
                                                error={
                                                    userProfileNameValidation &&
                                                    !userProfileName
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Role"
                                                name="role"
                                                value={role}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                                helperText="Access tier for this profile"
                                            >
                                                <MenuItem value="SUPER_ADMIN">
                                                    Super Admin
                                                </MenuItem>
                                                <MenuItem value="MANUFACTURER">
                                                    Manufacturer
                                                </MenuItem>
                                                <MenuItem value="DEALER">
                                                    Dealer
                                                </MenuItem>
                                                <MenuItem value="USER">
                                                    User
                                                </MenuItem>
                                            </TextField>
                                        </Grid>

                                        {role === 'MANUFACTURER' && (
                                                <Grid item xs={7}>
                                                    <Autocomplete
                                                        multiple
                                                        id="userProfileAssignedManufacturers"
                                                        value={
                                                            userProfileAssignedManufacturers
                                                        }
                                                        onBlur={(e) => {
                                                            setState({
                                                                formValidation:
                                                                    {
                                                                        ...formValidation,
                                                                        isFormDirty:
                                                                            true,
                                                                    },
                                                            })
                                                            validateOnBlur({
                                                                target: {
                                                                    name: 'userProfileAssignedManufacturers',
                                                                    value:
                                                                        userProfileAssignedManufacturers.length ===
                                                                        0
                                                                            ? ''
                                                                            : true,
                                                                },
                                                            })
                                                        }}
                                                        onChange={(
                                                            event,
                                                            newValue
                                                        ) => {
                                                            // single-manufacturer constraint: keep only the last pick
                                                            const single =
                                                                newValue.length
                                                                    ? [
                                                                          newValue[
                                                                              newValue.length -
                                                                                  1
                                                                          ],
                                                                      ]
                                                                    : []
                                                            handleChange({
                                                                target: {
                                                                    name: 'userProfileAssignedManufacturers',
                                                                    value: single,
                                                                },
                                                            })
                                                        }}
                                                        options={[
                                                            ...userProfileAssignedManufacturers,
                                                            ...manufacturers.filter(
                                                                ({
                                                                    manufacturerName,
                                                                }) =>
                                                                    !userProfileAssignedManufacturers.find(
                                                                        (
                                                                            brand
                                                                        ) =>
                                                                            brand.manufacturerName ===
                                                                            manufacturerName
                                                                    )
                                                            ),
                                                        ]}
                                                        getOptionLabel={(
                                                            option
                                                        ) =>
                                                            option.manufacturerName
                                                        }
                                                        renderTags={(
                                                            tagValue,
                                                            getTagProps
                                                        ) =>
                                                            tagValue.map(
                                                                (
                                                                    option: any,
                                                                    index
                                                                ) => (
                                                                    <Chip
                                                                        {...getTagProps(
                                                                            {
                                                                                index,
                                                                            }
                                                                        )}
                                                                        label={
                                                                            option.manufacturerName
                                                                        }
                                                                        disabled={
                                                                            userProfileAssignedManufacturers.indexOf(
                                                                                index
                                                                            ) !==
                                                                                -1 ||
                                                                            readOnly
                                                                        }
                                                                        onDelete={(
                                                                            e
                                                                        ) => {
                                                                            getTagProps(
                                                                                {
                                                                                    index,
                                                                                }
                                                                            ).onDelete(
                                                                                e
                                                                            )
                                                                            setState(
                                                                                {
                                                                                    formValidation:
                                                                                        {
                                                                                            ...formValidation,
                                                                                            isFormDirty:
                                                                                                true,
                                                                                        },
                                                                                }
                                                                            )
                                                                        }}
                                                                    />
                                                                )
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                name="userProfileAssignedManufacturers"
                                                                label="Manufacturer"
                                                                placeholder="Select the manufacturer for this profile"
                                                                disabled={
                                                                    readOnly
                                                                }
                                                                onBlur={
                                                                    validateOnBlur
                                                                }
                                                                helperText={
                                                                    userProfileAssignedManufacturers &&
                                                                    !userProfileAssignedManufacturers
                                                                        ? 'Please select a Manufacturer'
                                                                        : ''
                                                                }
                                                                error={
                                                                    userProfileAssignedManufacturersValidation &&
                                                                    !userProfileAssignedManufacturers
                                                                }
                                                                required={true}
                                                            />
                                                        )}
                                                        disabled={readOnly}
                                                    />
                                                </Grid>
                                            )}

                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    disableDeactivation
                                                                }
                                                                onChange={(e) =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                disableDeactivation:
                                                                                    e
                                                                                        .target
                                                                                        .checked,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                            />
                                                        }
                                                        label="Deactivation Protection"
                                                        key="switch.mobileCustomerProfile"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </FormSection>
                    </Grid>

                    {auth?.userProfile?.superAdmin && (
                        <Grid item md={!readOnly ? 6 : 12} xs={12}>
                            <FormSection
                                title="User Permissions"
                                titleAlignment="center"
                            >
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        {permissionNames.map((name) => (
                                                    <Fragment key={name}>
                                                        <Grid
                                                            item
                                                            md={6}
                                                            xs={6}
                                                        >
                                                            {name}
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            md={3}
                                                            xs={3}
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={
                                                                            userProfilePermissions &&
                                                                            userProfilePermissions[
                                                                                name
                                                                            ] &&
                                                                            userProfilePermissions[
                                                                                name
                                                                            ]
                                                                                ?.read
                                                                                ? userProfilePermissions[
                                                                                      name
                                                                                  ]
                                                                                      ?.read
                                                                                : false
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) => {
                                                                            const isChecked =
                                                                                event
                                                                                    .target
                                                                                    .checked

                                                                            handleUserPermissionsChange(
                                                                                isChecked,
                                                                                name,
                                                                                'read'
                                                                            )
                                                                        }}
                                                                        name={`${name}.read`}
                                                                        icon={
                                                                            <StickyNote2Icon />
                                                                        }
                                                                        checkedIcon={
                                                                            <DoneAllIcon />
                                                                        }
                                                                    />
                                                                }
                                                                label="Read"
                                                                disabled={
                                                                    readOnly
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            md={2}
                                                            xs={3}
                                                        >
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={
                                                                            userProfilePermissions &&
                                                                            userProfilePermissions[
                                                                                name
                                                                            ] &&
                                                                            userProfilePermissions[
                                                                                name
                                                                            ]
                                                                                ?.write
                                                                                ? userProfilePermissions[
                                                                                      name
                                                                                  ]
                                                                                      ?.read &&
                                                                                  userProfilePermissions[
                                                                                      name
                                                                                  ]
                                                                                      ?.write
                                                                                : false
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) => {
                                                                            const isChecked =
                                                                                event
                                                                                    .target
                                                                                    .checked

                                                                            handleUserPermissionsChange(
                                                                                isChecked,
                                                                                name,
                                                                                'write'
                                                                            )
                                                                        }}
                                                                        name={`${name}.write`}
                                                                        icon={
                                                                            <EditIcon />
                                                                        }
                                                                        checkedIcon={
                                                                            <DoneAllIcon />
                                                                        }
                                                                    />
                                                                }
                                                                label="Write"
                                                                disabled={
                                                                    readOnly
                                                                }
                                                            />
                                                        </Grid>
                                                    </Fragment>
                                                ))}
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {!readOnly && (
                <Grid item xs={12}>
                    <SaveButton
                        handleSubmit={handleSubmit}
                        handleReset={handleReset}
                        disabledSave={!isFormValid}
                        disabledReset={!isFormDirty}
                        saveTitle={'Save & Close'}
                    />
                </Grid>
            )}
        </Grid>
    )
}

export default UserProfileForm
